import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Container,
  Checkbox,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import ChecklistResults from '../components/ChecklistResults';

function LaunchChecklist() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [includePageSpeed, setIncludePageSpeed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handleCheck = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';

      const response = await fetch(`${apiUrl}/launch-checklist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: url.trim(), includePageSpeed }),
      });

      const data = await response.json();

      if (data.success) {
        setResults(data);
      } else {
        setError(data.error || 'Failed to run checklist');
      }
    } catch (err) {
      console.error('Checklist error:', err);
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResults(null);
    setError(null);
    setUrl('');
    setIncludePageSpeed(false);
  };

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    setIncludePageSpeed(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleCheck();
    }
  };

  return (
    <Box minH="100vh" bg="#0F1419">
      <Container maxW="1200px" py={8}>
        {/* Navigation */}
        <HStack mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={() => (results ? handleReset() : navigate('/'))}
            variant="ghost"
            colorScheme="purple"
            size="sm"
            color="gray.400"
            _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
          >
            {results ? 'New Check' : 'Back to Tools'}
          </Button>
        </HStack>

        {/* Header */}
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" mb={4}>
            <Heading as="h1" fontSize="4xl" fontWeight="bold" color="white" mb={2}>
              Launch Checklist
            </Heading>
            <Text fontSize="md" color="gray.400" fontWeight="medium">
              Audit websites for essential elements before going live
            </Text>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              status="error"
              borderRadius="md"
              bg="red.900"
              borderColor="red.700"
              borderWidth="1px"
            >
              <AlertIcon />
              <AlertDescription color="white">{error}</AlertDescription>
            </Alert>
          )}

          {/* Input Form */}
          {!results && !loading && (
            <Box
              bg="#1A1F2E"
              p={10}
              borderRadius="lg"
              boxShadow="2xl"
              borderWidth="1px"
              borderColor="gray.700"
              maxW="600px"
              mx="auto"
              w="100%"
            >
              <VStack spacing={6}>
                <Box w="100%">
                  <Text fontSize="sm" fontWeight="semibold" color="white" mb={3}>
                    Enter Website URL
                  </Text>
                  <Input
                    value={url}
                    onChange={handleUrlChange}
                    onKeyPress={handleKeyPress}
                    placeholder="https://example.com"
                    bg="#0F1419"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
                    }}
                    fontSize="md"
                    size="lg"
                  />
                  <Text color="gray.500" fontSize="xs" mt={2}>
                    Enter the full URL including https://
                  </Text>
                </Box>

                <Checkbox
                  isChecked={includePageSpeed}
                  onChange={(e) => setIncludePageSpeed(e.target.checked)}
                  colorScheme="purple"
                  size="md"
                >
                  <Text color="gray.300" fontSize="sm">
                    Include PageSpeed report (adds ~30 seconds)
                  </Text>
                </Checkbox>

                <Button
                  onClick={handleCheck}
                  size="lg"
                  width="full"
                  isDisabled={!url.trim()}
                  bg="purple.500"
                  color="white"
                  _hover={{
                    bg: 'purple.600',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  fontWeight="semibold"
                >
                  Run Checklist
                </Button>
              </VStack>
            </Box>
          )}

          {/* Loading State */}
          {loading && (
            <Box
              bg="#1A1F2E"
              p={10}
              borderRadius="lg"
              boxShadow="2xl"
              borderWidth="1px"
              borderColor="gray.700"
              maxW="600px"
              mx="auto"
              w="100%"
            >
              <VStack spacing={6}>
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor="gray.700"
                  color="purple.500"
                  size="xl"
                />
                <VStack spacing={2}>
                  <Text color="white" fontSize="lg" fontWeight="semibold">
                    Running checks...
                  </Text>
                  <Text color="gray.400" fontSize="sm" textAlign="center">
                    {includePageSpeed
                      ? 'This may take up to 30 seconds while we fetch PageSpeed data'
                      : 'This should only take a few seconds'}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          )}

          {/* Results */}
          {results && <ChecklistResults results={results} />}
        </VStack>
      </Container>
    </Box>
  );
}

export default LaunchChecklist;
